'use client';

import { useRef } from 'react';

import { BaseFaqItemFragment } from '@flipster/features/src/contentful/faq/query.generated';
import ContentfulRichText from '@flipster/features/src/shared/contentful-rich-text';
import { Accordion } from '@flipster/features/src/shared/faq-accordion';
import { useZendesk } from '@flipster/features/src/shared/zendesk/use-zendesk';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { TextButton } from '@flipster/react/design-system/text-button';
import { Styles, css } from '@flipster/styled-system/css';
import { center, stack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';

import { maxWStylesWithMargin, sectionLargeHeadingStyles } from '@web/app/[locale]/(web)/_main-page/_styles';
import { useMainPageIntersectionSegment } from '@web/app/[locale]/(web)/_main-page/hooks/useIntersectionSegment';
import { YoutubeIcon } from '@web/assets/svgs/main-page';

interface Props {
  faqs?: BaseFaqItemFragment[];
  css?: Styles;
}

export const Faq = ({ faqs, css: cssProp }: Props) => {
  const intersectionRef = useRef<HTMLHeadingElement>(null);
  useMainPageIntersectionSegment(intersectionRef, 'Frequently asked questions');
  const { openMessenger } = useZendesk();

  return (
    <section className={css(maxWStylesWithMargin, cssProp)}>
      <h2 ref={intersectionRef} className={css(sectionLargeHeadingStyles, { mb: { base: 32, tablet: 40 } })}>
        <FormattedMessage id='Common.r251104.Frequently_asked_questions' />
      </h2>

      <Accordion.Root headingLevel={2} css={{ mb: { base: 32, tablet: 40 } }}>
        {faqs?.map((faq, index) => (
          <Accordion.Item key={faq.title}>
            <Accordion.Header index={index + 1}>{faq.title}</Accordion.Header>
            <Accordion.Panel>
              <ContentfulRichText startHeadingLevel={3} richTextDocument={faq.description} />
              {!!faq.youtubeCollection?.items?.length && (
                <div className={stack({ alignItems: 'flex-start', mt: 22, gap: 2 })}>
                  {faq.youtubeCollection?.items.map((y) => {
                    if (!y?.title || !y?.url) return null;
                    return (
                      <TextButton
                        color='accent'
                        size='large'
                        as='a'
                        href={y.url}
                        newTab
                        icon={<YoutubeIcon />}
                        label={y.title}
                        key={y.url}
                      />
                    );
                  })}
                </div>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion.Root>

      <div className={center()}>
        <Button
          hierarchy='primary'
          color='accent'
          size='large'
          label={<FormattedMessage id='Common.r251104.More_questions' />}
          onClick={() => {
            segment.track(MainEvent.MainPageChatClick);
            openMessenger();
          }}
          css={{ minW: 154 }}
        />
      </div>
    </section>
  );
};
